import AppPanel from '../fw/AppPanel.react';
import React from 'react';
import PropTypes from 'prop-types';
import Relay from 'react-relay';
import {injectIntl, defineMessages, FormattedMessage, IntlShape} from 'react-intl';
import {wrapNotFound} from '../app/NotFoundHandler.react';
import types from '../../common/model-api/types';
import {getLastOrganisationId} from '../app/LastOrganisationId';
import PagingGrid, {defaultPageSize, processPagingParams} from '../fw/PagingGrid.react';
import urlMapping from '../app/URLMapping';
import EditorWithSaver from '../fw/EditorWithSaver.react';
import {declareEditEntity} from '../../common/fw/MetaDataHelper.ts';
import EditorSaver from '../fw/EditorSaver';
import {getFlowTitle} from './FlowConfig.react';
import { User } from '../../common/types/types';

const messages = defineMessages({
    title: {id: 'FlowCoordination.title', 'defaultMessage': 'Coordination'},
    save: {id: 'action.saveCoordinations', 'defaultMessage': 'Save coordination'},
});

interface FlowCoordinationProps {
    intl: IntlShape;
    relay: object;
    user: User;
}

class FlowCoordination extends React.Component<FlowCoordinationProps> {

    typeName = 'flowCoordinationEvents';

    static propTypes = {
        intl: PropTypes.object.isRequired,
        relay: PropTypes.object.isRequired,
        user: PropTypes.object.isRequired,
    };
    static contextTypes = {
        router: PropTypes.object
    };

    constructor(props) {
        super(props);

        let flow = this.props.user.organisation.flow;
        let flowCoordinationEvents = flow.flowDef.flowCoordinationEvents;

        this.originalOptions = { ...JSON.parse(flow.coordinationListeners) };

        this.metadata = this.collectMetaData(flow, flowCoordinationEvents.edges.map(({node}) => node));
    }

    collectMetaData(flow, flowCoordinationEvents) {
        let list = {};

        if (flowCoordinationEvents) {
            if (flowCoordinationEvents && flowCoordinationEvents.length > 0) {
                for (let event of flowCoordinationEvents) {
                    let matching = [];
                    for (let subFlow of flow.coordinationFlowsToNotify.edges.map(({node}) => node)) {
                        let hooks = subFlow.flowDef.flowCoordinationHooks.edges.map(({node}) => node);
                        for (let hook of hooks) {
                            if (hook.coordinationDef_id === event.coordinationDef_id) {
                                matching.push({hook: hook, ...subFlow, id: subFlow.realId, value: subFlow.realId});
                            }
                        }
                    }

                    list[event.realId] = {
                        name: event.name || event.coordinationDef.name,
                        type: 'relations',
                        description: event.description || event.coordinationDef.description,
                        values: matching,
                        to: () => types['flows'],
                        display: (item) => {
                            let flowTitle = getFlowTitle(item);
                            let name = item['hook']['name'] || item['hook']['coordinationDef']['name'] || '';
                            return flowTitle + ' - ' + name;
                        },
                    };
                }
            }
        }

        return declareEditEntity({
            entity: "options",
            graphQL: "Options",
            name: this.props.intl.formatMessage(messages.title),
            fields: (types) => {
                return list;
            }
        });
    }

	// eslint-disable-next-line max-params
    _saver = (metadata, isCreate, columns, data) => {
        /* we have to find which options are authentication and which not */
        let {flow} = this.props.user.organisation;

        let fatQuery = Relay.QL `fragment on UpdateFlowPayload {
                                updatedNode {
                                    id,
                                    coordinationListeners,
                                },
                            }`;

        let mutation = Relay.QL `mutation { updateFlow }`;


		// eslint-disable-next-line max-params
        let saver = new EditorSaver(metadata, mutation, fatQuery, null, isCreate ? EditorSaver.CREATE : null, (data, storeData, defaultValue, columns) => {

            storeData['coordinationListeners'] = JSON.stringify({...JSON.parse(flow.coordinationListeners), ...data});
            storeData['id'] = flow['id'];

            return storeData;
        });

        return saver;
    };

    _onSuccess = () => {
        let {flow} = this.props.user.organisation;

        let path = urlMapping.getUrl(types['flows'], "view", flow);
        this.context.router.push(path);
    };

    render() {
        let { user } = this.props;
        let { flow } = user.organisation;
        let type = types[this.typeName];
        let list = flow.flowDef.flowCoordinationEvents;

        return (
            <AppPanel breadcrumbParams={[
                {type: types['organisations'], data: this.props.user.organisation},
                {type: types['flows'], data: this.props.user.organisation.flow}
            ]} title={this.props.intl.formatMessage(messages.title)}>

                    <p><FormattedMessage
                                            defaultMessage="Flow coordination allows to chain multiple flows together. When flow is triggered, it can trigger other flows. This allows to invoke expedition process after download of orders."
                                            id="flowCoordination.explanation"/></p>

                    <EditorWithSaver
						buttonTitle={this.props.intl.formatMessage(messages.save, {'entity': this.metadata.pluralName()})}
						data={this.originalOptions}
						metadata={this.metadata}
						onSuccess={this._onSuccess}
						saver={this._saver}
						urlMapping={urlMapping}
					>
					</EditorWithSaver>

            </AppPanel>
        );
    }
}

export default Relay.createContainer(wrapNotFound(injectIntl(FlowCoordination), (props) => {
    return props.user.organisation && props.user.organisation.flow;
}), {
    initialVariables: {
        organisationId: '',
        flowId: '',
        first: defaultPageSize,
        after: null,
        before: null,
        last: null,
    },
    prepareVariables (variables) {
        processPagingParams(variables);

        if (!variables.organisationId) {
			variables.organisationId = getLastOrganisationId();
		}

        return variables;
    },
    fragments: {
        user: () => Relay.QL`
            fragment on User {
                organisation(id: $organisationId) {
                    id,
                    name,
                    flow(id: $flowId) {
                        id,
                        realId,
                        organisation_id,
                        name,
                        coordinationListeners,
                        coordinationFlowsToNotify(first: $first, after: $after, before: $before, last: $last) {
                            edges {
                                node {
                                    id,
                                    realId,
                                    name,
                                    selectedChannels,
                                    channels(first: 100) {
                                        edges {
                                            node {
                                                id,
                                                realId,
                                                name,
                                                channelDef {
                                                    id,
                                                    realId,
                                                    name,
                                                    icon,
                                                    defaultName,
                                                }
                                            }
                                        }
                                    },
                                    flowDef {
                                        id,
                                        name,
                                        title,
                                        flowCoordinationHooks(first: $first, after: $after, before: $before, last: $last) {
                                            edges {
                                                node {
                                                    id,
                                                    realId,
                                                    description,
                                                    coordinationDef_id,
                                                    coordinationDef {
                                                        id,
                                                        realId,
                                                        name,
                                                    }
                                                }
                                            },
                                            pageInfo {
                                                hasNextPage,
                                                hasPreviousPage,
                                                startCursor,
                                                endCursor
                                            }    
                                        }
                                    }
                                }
                            },
                            pageInfo {
                                hasNextPage,
                                hasPreviousPage,
                                startCursor,
                                endCursor
                            }
                        },
                        flowDef {
                            id,
                            realId,
                            name,
                            flowCoordinationEvents(first: $first, after: $after, before: $before, last: $last) {
                                edges {
                                    node {
                                        id,
                                        realId,
                                        name,
                                        description,
                                        coordinationDef_id,
                                        coordinationDef {
                                            id,
                                            realId,
                                            name,
                                        },
                                    }
                                },
                                pageInfo {
                                    hasNextPage,
                                    hasPreviousPage,
                                    startCursor,
                                    endCursor
                                }    
                            }
                        },
                    }
                }
            }
        `,
    },
});
